import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import {
	isGR6_2SectionA,
	isGR6_2SectionB,
	isGR6_2SectionC,
	isGR6_2SectionD,
	isGR6_2SectionE,
	isGR6_2SectionF,
	isResidentialLandPermitGR6_2
} from '../__index.utils'

export const STEP4_2FIELDS = {
	typesOfWorkResidentialLand: 'typesOfWorkResidentialLand',
	areaOfWorkPreparationTerrain: 'areaOfWorkPreparationTerrain',
	// areaOfWorkUnitPreparationTerrain: 'areaOfWorkUnitPreparationTerrain',
	areaOfWorkRemblaiDeblaiDecontamination: 'areaOfWorkRemblaiDeblaiDecontamination',
	// areaOfWorkUnitRemblaiDeblaiDecontamination: 'areaOfWorkUnitRemblaiDeblaiDecontamination',
	isWallAdditionOrRelocationMuret: 'isWallAdditionOrRelocationMuret',
	heightWallMuret: 'heightWallMuret',
	// heightUnitWallMuret: 'heightUnitWallMuret',
	lengthWallMuret: 'lengthWallMuret',
	// lengthUnitWallMuret: 'lengthUnitWallMuret',
	materialExistingWallMuret: 'materialExistingWallMuret',
	materialProjectedWallMuret: 'materialProjectedWallMuret',
	isWallAdditionOrRelocationMurSoutenement: 'isWallAdditionOrRelocationMurSoutenement',
	heightWallMurSoutenement: 'heightWallMurSoutenement',
	// heightUnitWallMurSoutenement: 'heightUnitWallMurSoutenement',
	lengthWallMurSoutenement: 'lengthWallMurSoutenement',
	// lengthUnitWallMurSoutenement: 'lengthUnitWallMurSoutenement',
	materialExistingWallMurSoutenement: 'materialExistingWallMurSoutenement',
	materialProjectedWallMurSoutenement: 'materialProjectedWallMurSoutenement',
	areaOfWorkBandeTampon: 'areaOfWorkBandeTampon',
	// areaOfWorkUnitBandeTampon: 'areaOfWorkUnitBandeTampon',
	areaOfWorkAmenagementPaysager: 'areaOfWorkAmenagementPaysager',
	// areaOfWorkUnitAmenagementPaysager: 'areaOfWorkUnitAmenagementPaysager',
} as const

export type Step4_2Fields = keyof typeof STEP4_2FIELDS

/**
 * useStep4_2State - Custom hook to manage the state for step 4_2 of the GR6 Land Development Work Permit form.
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 4_2.
 */

// --- 4.2. State HOOK for Step 4. ---
// This hook is used to manage the state of Step 4.2 in the GR6.2
export function useStep4_2State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	//! GR6.2 is ResidentialLand

	const isGR6_2 = isResidentialLandPermitGR6_2(answers?.whatToDo)

	return {
		// Quel(s) type(s) de travaux souhaitez-vous effectuer?
		// Terrain Résidentiel
		typesOfWorkResidentialLand: {
			name: 'typesOfWorkResidentialLand',
			label:
				pageAssets?.request_form_residentialRenovationOrRepairPermit_typesOfWork,
			labelKey: 'request_form_residentialRenovationOrRepairPermit_typesOfWork',
			value: answers?.typesOfWorkResidentialLand?.toString() || '',
			required: isGR6_2,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// *** Section A= Préparation de terrain et/ou excavation
		// Superficie des travaux
		areaOfWorkPreparationTerrain: {
			name: 'areaOfWorkPreparationTerrain',
			label: pageAssets?.request_form_landDevWorkPermit_areaOfWork,
			labelKey: 'request_form_landDevWorkPermit_areaOfWork',
			value: answers?.areaOfWorkPreparationTerrain?.toString() || '',
			required: isGR6_2 && isGR6_2SectionA(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		areaOfWorkUnitPreparationTerrain: {
			name: 'areaOfWorkUnitPreparationTerrain',
			label: pageAssets?.request_form_area_unit,
			labelKey: 'request_form_area_unit',
			value:
				answers?.areaOfWorkUnitPreparationTerrain?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// *** Section B= Remblai, déblai ou décontamination du sol
		// Superficie des travaux
		areaOfWorkRemblaiDeblaiDecontamination: {
			name: 'areaOfWorkRemblaiDeblaiDecontamination',
			label: pageAssets?.request_form_landDevWorkPermit_areaOfWork,
			labelKey: 'request_form_landDevWorkPermit_areaOfWork',
			value: answers?.areaOfWorkRemblaiDeblaiDecontamination?.toString() || '',
			required: isGR6_2 && isGR6_2SectionB(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		areaOfWorkUnitRemblaiDeblaiDecontamination: {
			name: 'areaOfWorkUnitRemblaiDeblaiDecontamination',
			label: pageAssets?.request_form_area_unit,
			labelKey: 'request_form_area_unit',
			value:
				answers?.areaOfWorkUnitRemblaiDeblaiDecontamination?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// *** Section C= Ajout ou déplacement d'un muret
		// Effectuez-vous l'ajout ou le déplacement d'un muret?
		isWallAdditionOrRelocationMuret: {
			name: 'isWallAdditionOrRelocationMuret',
			label:
				pageAssets?.request_form_landDevWorkPermit_isWallAdditionOrRelocationMuret,
			labelKey:
				'request_form_landDevWorkPermit_isWallAdditionOrRelocationMuret',
			value: answers?.isWallAdditionOrRelocationMuret?.toString() || '',
			required: isGR6_2 && isGR6_2SectionC(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		//Hauteur du muret
		heightWallMuret: {
			name: 'heightWallMuret',
			label: pageAssets?.request_form_landDevWorkPermit_heightWallMuret,
			labelKey: 'request_form_landDevWorkPermit_heightWallMuret',
			value: answers?.heightWallMuret?.toString() || '',
			required: isGR6_2 && isGR6_2SectionC(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		heightUnitWallMuret: {
			name: 'heightUnitWallMuret',
			label: pageAssets?.request_form_height_unit,
			labelKey: 'request_form_height_unit',
			value:
				answers?.heightUnitWallMuret?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Longueur du muret
		lengthWallMuret: {
			name: 'lengthWallMuret',
			label: pageAssets?.request_form_landDevWorkPermit_lengthWallMuret,
			labelKey: 'request_form_landDevWorkPermit_lengthWallMuret',
			value: answers?.lengthWallMuret?.toString() || '',
			required: isGR6_2 && isGR6_2SectionC(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		lengthUnitWallMuret: {
			name: 'lengthUnitWallMuret',
			label: pageAssets?.request_form_length_unit,
			labelKey: 'request_form_length_unit',
			value:
				answers?.lengthUnitWallMuret?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Matériau existant
		materialExistingWallMuret: {
			name: 'materialExistingWallMuret',
			label: pageAssets?.request_form_landDevWorkPermit_materialExistingWall,
			labelKey: 'request_form_landDevWorkPermit_materialExistingWall',
			value: answers?.materialExistingWallMuret || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Matériau projeté
		materialProjectedWallMuret: {
			name: 'materialProjectedWallMuret',
			label: pageAssets?.request_form_landDevWorkPermit_materialProjectedWall,
			labelKey: 'request_form_landDevWorkPermit_materialProjectedWall',
			value: answers?.materialProjectedWallMuret || '',
			required: isGR6_2 && isGR6_2SectionC(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// *** Section D= Ajout ou déplacement d'un mur de soutènement
		// Effectuez-vous l'ajout ou le déplacement d'un mur de soutènement?
		isWallAdditionOrRelocationMurSoutenement: {
			name: 'isWallAdditionOrRelocationMurSoutenement',
			label:
				pageAssets?.request_form_landDevWorkPermit_isWallAdditionOrRelocationMurSoutenement,
			labelKey:
				'request_form_landDevWorkPermit_isWallAdditionOrRelocationMurSoutenement',
			value:
				answers?.isWallAdditionOrRelocationMurSoutenement?.toString() || '',
			required: isGR6_2 && isGR6_2SectionD(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// Hauteur du mur de soutènement
		heightWallMurSoutenement: {
			name: 'heightWallMurSoutenement',
			label:
				pageAssets?.request_form_landDevWorkPermit_heightWallMurSoutenement,
			labelKey: 'request_form_landDevWorkPermit_heightWallMurSoutenement',
			value: answers?.heightWallMurSoutenement?.toString() || '',
			required: isGR6_2 && isGR6_2SectionD(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		heightUnitWallMurSoutenement: {
			name: 'heightUnitWallMurSoutenement',
			label: pageAssets?.request_form_height_unit,
			labelKey: 'request_form_height_unit',
			value:
				answers?.heightUnitWallMurSoutenement?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// Longueur du mur de soutènement
		lengthWallMurSoutenement: {
			name: 'lengthWallMurSoutenement',
			label:
				pageAssets?.request_form_landDevWorkPermit_lengthWallMurSoutenement,
			labelKey: 'request_form_landDevWorkPermit_lengthWallMurSoutenement',
			value: answers?.lengthWallMurSoutenement?.toString() || '',
			required: isGR6_2 && isGR6_2SectionD(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		lengthUnitWallMurSoutenement: {
			name: 'lengthUnitWallMurSoutenement',
			label: pageAssets?.request_form_length_unit,
			labelKey: 'request_form_length_unit',
			value:
				answers?.lengthUnitWallMurSoutenement?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// Matériau existant
		materialExistingWallMurSoutenement: {
			name: 'materialExistingWallMurSoutenement',
			label: pageAssets?.request_form_landDevWorkPermit_materialExistingWall,
			labelKey: 'request_form_landDevWorkPermit_materialExistingWall',
			value: answers?.materialExistingWallMurSoutenement || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// Matériau projeté
		materialProjectedWallMurSoutenement: {
			name: 'materialProjectedWallMurSoutenement',
			label: pageAssets?.request_form_landDevWorkPermit_materialProjectedWall,
			labelKey: 'request_form_landDevWorkPermit_materialProjectedWall',
			value: answers?.materialProjectedWallMurSoutenement || '',
			required: isGR6_2 && isGR6_2SectionD(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// *** Section E= Bande tampon
		// Superficie des travaux
		areaOfWorkBandeTampon: {
			name: 'areaOfWorkBandeTampon',
			label: pageAssets?.request_form_landDevWorkPermit_areaOfWork,
			labelKey: 'request_form_landDevWorkPermit_areaOfWork',
			value: answers?.areaOfWorkBandeTampon?.toString() || '',
			required: isGR6_2 && isGR6_2SectionE(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		areaOfWorkUnitBandeTampon: {
			name: 'areaOfWorkUnitBandeTampon',
			label: pageAssets?.request_form_area_unit,
			labelKey: 'request_form_area_unit',
			value:
				answers?.areaOfWorkUnitBandeTampon?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// *** Section F= Aménagement paysager et/ou gazonnement
		// Superficie des travaux
		areaOfWorkAmenagementPaysager: {
			name: 'areaOfWorkAmenagementPaysager',
			label: pageAssets?.request_form_landDevWorkPermit_areaOfWork,
			labelKey: 'request_form_landDevWorkPermit_areaOfWork',
			value: answers?.areaOfWorkAmenagementPaysager?.toString() || '',
			required: isGR6_2 && isGR6_2SectionF(answers?.typesOfWorkResidentialLand),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		areaOfWorkUnitAmenagementPaysager: {
			name: 'areaOfWorkUnitAmenagementPaysager',
			label: pageAssets?.request_form_area_unit,
			labelKey: 'request_form_area_unit',
			value:
				answers?.areaOfWorkUnitAmenagementPaysager?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		}
	}
}
